import { useState } from 'react';
import { useFetch, useModal, useRequest } from '../../../../hooks';
import { notify } from '../../../../app';
import {
  getSipProvidersList,
  createSipProvider,
  updateSipProvider,
} from '../../../../api/sip-providers';
import type { ExternalSipProviderDto } from '../../../../api/sip-providers';
import type { SipProviderFormData } from '../types';

const defaultFormValues: SipProviderFormData = {
  name: '',
  description: '',
};

const mapProviderToFormData = (
  provider: ExternalSipProviderDto
): SipProviderFormData => ({
  name: provider.name || '',
  description: provider.description || '',
});

export const useSipProvidersManagement = () => {
  const { isOpened, onClose, onOpen } = useModal();
  const {
    isOpened: isOpenedEditModal,
    onClose: onCloseEditModal,
    onOpen: onOpenEditModal,
  } = useModal();

  const [selectedProvider, setSelectedProvider] =
    useState<ExternalSipProviderDto | null>(null);
  const [formValues, setFormValues] =
    useState<SipProviderFormData>(defaultFormValues);

  const {
    data: providers,
    loading,
    refresh,
  } = useFetch(async () => {
    return await getSipProvidersList();
  }, []);

  const [handleCreateProvider, creatingProvider] = useRequest(
    async (_signal, payload: SipProviderFormData) => {
      return await createSipProvider({
        name: payload.name,
        description: payload.description || undefined,
      });
    },
    {
      onSuccess: () => {
        notify.success('SIP provider created successfully');
        refresh();
        onClose();
      },
      onError: () => {
        notify.error('Failed to create SIP provider');
      },
    }
  );

  const [handleUpdateProvider, updatingProvider] = useRequest(
    async (_signal, payload: { id: string; data: SipProviderFormData }) => {
      return await updateSipProvider(payload.id, {
        name: payload.data.name,
        description: payload.data.description || undefined,
      });
    },
    {
      onSuccess: () => {
        notify.success('SIP provider updated successfully');
        refresh();
        onCloseEditModal();
        setSelectedProvider(null);
      },
      onError: () => {
        notify.error('Failed to update SIP provider');
      },
    }
  );

  const handleCreateProviderSubmit = (data: SipProviderFormData) => {
    handleCreateProvider(data);
  };

  const handleEditProviderSubmit = (data: SipProviderFormData) => {
    if (selectedProvider?.id) {
      handleUpdateProvider({
        id: selectedProvider.id,
        data,
      });
    }
  };

  const handleOpenCreateModal = () => {
    setFormValues(defaultFormValues);
    onOpen();
  };

  const handleCloseCreateModal = () => {
    onClose();
    setFormValues(defaultFormValues);
  };

  const handleEditClick = (provider: ExternalSipProviderDto) => {
    setSelectedProvider(provider);
    setFormValues(mapProviderToFormData(provider));
    onOpenEditModal();
  };

  const handleCloseEditModal = () => {
    onCloseEditModal();
    setSelectedProvider(null);
    setFormValues(defaultFormValues);
  };

  return {
    providers: (providers || []) as ExternalSipProviderDto[],
    loading,
    selectedProvider,
    formValues,
    setFormValues,
    isOpened,
    onOpen: handleOpenCreateModal,
    onClose: handleCloseCreateModal,
    isOpenedEditModal,
    onCloseEditModal: handleCloseEditModal,
    handleCreateProviderSubmit,
    handleEditProviderSubmit,
    handleEditClick,
    creatingProvider,
    updatingProvider,
  };
};
